
import $ from 'jquery';
import _ from 'underscore';
import Backbone from "backbone";
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Player } from '@remotion/player';
import Utils from "models/helper/Utils";
import RolloutHelper from "models/helper/FeatureRolloutHelper";
import VideoUtilsHelper from "helper/VideoUtilsHelper";
import { VideoTrimPreview } from "components/audioEditor/VideoTrimPreview";
import VideoBufferRegionSelectorTemplate from 'text!../../../templates/audioEditor/VideoBufferRegionSelectorTemplate.html';
import ti18n from 'i18n!nls/Sequenceri18n';
import RSVP from 'rsvp';

const Rollout = RolloutHelper.getInstance();
const SELECTED_REGION_ID = "selectedRegion";

// Returns the OPFS-cached blob URL if available, otherwise the CloudFront-converted URL.
// Never returns a raw S3 URL, because raw S3 triggers uncached origin egress.
function _getBestVideoUrl(model) {
    // Check for a cached blob URL from the sequencer node scheduling info
    var schedulingInfo = model && model.getSchedulingInfo && model.getSchedulingInfo();
    if (schedulingInfo && schedulingInfo.getCachedBlobUrl) {
        var blobUrl = schedulingInfo.getCachedBlobUrl();
        if (blobUrl) return blobUrl;
    }
    // Fall back to CloudFront-converted URL
    var rawUrl = model && model.videoUrl;
    var cloudFrontUrl = Utils.getInstance().getCloudFrontUrlIfApplicable(rawUrl);
    if (cloudFrontUrl && cloudFrontUrl !== rawUrl) return cloudFrontUrl;

    // Fail closed for unmapped S3 URLs: returning the raw origin would
    // reintroduce uncached egress, which this function exists to prevent.
    var isS3Url = typeof rawUrl === 'string' && rawUrl.indexOf('amazonaws.com') !== -1;
    if (isS3Url) return null;

    return rawUrl;
}

const VideoBufferRegionSelectorView = Backbone.View.extend({

		initialize : function(options) {
			this.model = options.model;
			this.videoUniqueId = options.videoUniqueId;
			this.videoType = options.videoType;
			this.sequencerSettings = options.sequencerSettings;
			this.compiledTemplate =  _.template(VideoBufferRegionSelectorTemplate);

			// Video timeline state
			this._videoDuration = 0;
			this._startTime = 0; // in seconds
			this._endTime = 0; // in seconds
			this._isDragging = false;
			this._dragHandle = null; // 'start' or 'end'
			this._timelineWidth = 0;
			this._thumbnailsGenerated = false;
			this._lastSeekTime = 0; // For throttling video seeks

			// Remotion Player state
			this._playerReactRoot = null;
			this._fps = 30; // Default fps, can be detected from video
			this._videoUrl = null;

			// Playback cursor state
			this._currentPlaybackTime = 0; // Current cursor position in seconds
			this._playerRef = null; // Reference to Remotion Player for imperative control
		this._isPlaying = false; // Track whether video is currently playing
		},

		events : {
			"click #playRegionBtn": "onPlayRegionButtonClicked",
			"click #pauseRegionBtn": "stopPlayback",
			"click #saveTrimBtn":"onSaveTrimButtonClicked",
			"mousedown .video_timeline_handle_start": "onStartHandleMouseDown",
			"mousedown .video_timeline_handle_end": "onEndHandleMouseDown",
			"click .video_buffer_timeline_container": "onTimelineClick",
		},

	onPlayRegionButtonClicked: function(){
		if(!this._playerRef) return;

		var currentTime = this._currentPlaybackTime || this._startTime;

		// If cursor is at or past the end, play from start
		if(currentTime >= this._endTime){
			currentTime = this._startTime;
			this._currentPlaybackTime = this._startTime;
			this._updatePlayCursorPosition(this._startTime);
		}


	// Set playing state FIRST (before player operations to prevent monitoring interference)
	this._isPlaying = true;
		// Seek Remotion Player to current time and play
		var frameNumber = this._calculateFrameFromTime(currentTime);
		this._playerRef.seekTo(frameNumber);
		this._playerRef.play();

		// Setup monitoring to track playback and stop at end
		this._setupPlaybackMonitoring();

	// Add CSS class to toggle play/pause button visibility
	this.$el.addClass("region_playing");
	},


		onTimelineClick: function(e){
			// Don't handle clicks on handles (they have their own mousedown handlers)
			if($(e.target).closest('.video_timeline_handle').length > 0){
				return;
			}

			// Calculate clicked position
			var containerOffset = this.videoTimelineContainer$el.offset();
			var relativeX = e.pageX - containerOffset.left;
			var percentPosition = Math.max(0, Math.min(1, relativeX / this._timelineWidth));
			var clickedTime = percentPosition * this._videoDuration;

			// Clamp to valid range within trim region
			clickedTime = Math.max(this._startTime, Math.min(this._endTime, clickedTime));

			// Move playback cursor to clicked position
			this._currentPlaybackTime = clickedTime;
			this._updatePlayCursorPosition(clickedTime);
		},

	_setupPlaybackMonitoring: function(){
		if(this._playbackMonitorInterval){
			clearInterval(this._playbackMonitorInterval);
		}

		this._playbackMonitorInterval = setInterval((function(){
			if(this._playerRef && this._isPlaying){
				// Get current frame from Remotion Player
				var currentFrame = this._playerRef.getCurrentFrame();
				// Convert frame to time (frame / fps + startTime)
				var currentTime = (currentFrame / this._fps) + this._startTime;

				// Update play cursor position (visual update only, no seeking)
				this._updatePlayCursorPosition(currentTime);

				// Stop playback when reaching end time
				if(currentTime >= this._endTime){
					this.stopPlayback();
				}
			}
		}).bind(this), Math.floor(1000 / this._fps)); // Match video fps (e.g., 33ms for 30fps)
	},

		_updatePlayCursorPosition: function(currentTime){
			if(!this._videoDuration) return;

			// Update state
			this._currentPlaybackTime = currentTime;

			var percentPosition = (currentTime / this._videoDuration) * 100;

			this.$el.find('.video_timeline_play_cursor').css({
				'left': percentPosition + '%',
				'display': 'block' // Always show cursor
			});

			// Update time label on cursor
			var timeText = Utils.getInstance().formatPlaybackPositionToString(currentTime, true);
			this.$el.find('.video_timeline_play_cursor_time').text(timeText);

			// Update Remotion Player to show frame at cursor position
			if(this._playerRef && !this._isPlaying){
				var frameNumber = this._calculateFrameFromTime(currentTime);
				this._playerRef.seekTo(frameNumber);
				this._playerRef.pause(); // Pause at this frame during trim adjustment
			}
		},

		// Alias for backward compatibility
		_updatePlaybackCursorPosition: function(currentTime){
			this._updatePlayCursorPosition(currentTime);
		},

		_calculateFrameFromTime: function(absoluteTime){
			// absoluteTime is in the full video timeline (0 to _videoDuration)
			// Convert to frame number within the trimmed segment (0 to durationInFrames)

			var timeInSegment = absoluteTime - this._startTime;
			var frameInSegment = Math.floor(timeInSegment * this._fps);
			var durationInFrames = Math.ceil((this._endTime - this._startTime) * this._fps);

			// Clamp to valid range
			return Math.max(0, Math.min(frameInSegment, durationInFrames - 1));
		},

		onStartHandleMouseDown: function(e){
			e.preventDefault();
			this._isDragging = true;
			this._dragHandle = 'start';
			this._setupDragListeners();

			// Show tooltip
			this._showDragTooltip(e.pageX, e.pageY, this._startTime);
		},

		onEndHandleMouseDown: function(e){
			e.preventDefault();
			this._isDragging = true;
			this._dragHandle = 'end';
			this._setupDragListeners();

			// Show tooltip
			this._showDragTooltip(e.pageX, e.pageY, this._endTime);
		},

		_setupDragListeners: function(){
			$(document).on('mousemove.videotrimmer', this._onDocumentMouseMove.bind(this));
			$(document).on('mouseup.videotrimmer', this._onDocumentMouseUp.bind(this));
		},

		_removeDragListeners: function(){
			$(document).off('mousemove.videotrimmer');
			$(document).off('mouseup.videotrimmer');
		},

		_onDocumentMouseMove: function(e){
			if(!this._isDragging) return;

			var containerOffset = this.videoTimelineContainer$el.offset();
			var relativeX = e.pageX - containerOffset.left;
			var percentPosition = Math.max(0, Math.min(1, relativeX / this._timelineWidth));
			var newTime = percentPosition * this._videoDuration;

			if(this._dragHandle === 'start'){
				// Ensure start doesn't go past end
				this._startTime = Math.min(newTime, this._endTime - 0.1);
				this._currentPlaybackTime = this._startTime; // Move cursor to start position
				this._updateDragTooltip(e.pageX, e.pageY, this._startTime);
			}else if(this._dragHandle === 'end'){
				// Ensure end doesn't go before start
				this._endTime = Math.max(newTime, this._startTime + 0.1);
				this._currentPlaybackTime = this._endTime; // Move cursor to end position
				this._updateDragTooltip(e.pageX, e.pageY, this._endTime);
			}

			this._updateHandlePositions();
			this.onRegionUpdated();

			// Update playback cursor position and Remotion Player
			this._updatePlaybackCursorPosition(this._currentPlaybackTime);

		// Update thumbnail at current position (QuickTime-style)
		this._updateThumbnailAtPosition(this._currentPlaybackTime);
		},

		_onDocumentMouseUp: function(e){
			this._isDragging = false;
			this._dragHandle = null;
			this._removeDragListeners();

			// Hide tooltip
			this._hideDragTooltip();
		},

		_updateHandlePositions: function(){
			var startPercent = (this._startTime / this._videoDuration) * 100;
			var endPercent = (this._endTime / this._videoDuration) * 100;

			// Update selected region position and size
			this.$el.find('.video_timeline_region').css({
				'left': startPercent + '%',
				'width': (endPercent - startPercent) + '%'
			});

			// Update trimmed-out overlay positions (gray areas)
			this.$el.find('.video_timeline_trimmed_left').css({
				'width': startPercent + '%'
			});

			this.$el.find('.video_timeline_trimmed_right').css({
				'width': (100 - endPercent) + '%'
			});

			// Update Remotion Player with new segment times
			this._renderRemotionPlayer();
		},

		_showDragTooltip: function(pageX, pageY, time){
			var tooltip = this.$el.find('.video_timeline_drag_tooltip');
			var timeText = Utils.getInstance().formatPlaybackPositionToString(time, true);

			tooltip.find('.video_timeline_drag_tooltip_time').text(timeText);
			tooltip.css({
				'left': pageX + 'px',
				'top': (pageY - 40) + 'px',
				'display': 'block'
			});
		},

		_updateDragTooltip: function(pageX, pageY, time){
			var tooltip = this.$el.find('.video_timeline_drag_tooltip');
			var timeText = Utils.getInstance().formatPlaybackPositionToString(time, true);

			tooltip.find('.video_timeline_drag_tooltip_time').text(timeText);
			tooltip.css({
				'left': pageX + 'px',
				'top': (pageY - 40) + 'px'
			});
		},

		_hideDragTooltip: function(){
			this.$el.find('.video_timeline_drag_tooltip').css('display', 'none');
		},

		_renderRemotionPlayer: function(){
			if(!this._videoUrl || !this._videoDuration) return;

			const container = this.remotionPlayerContainer$el;
			if(!container || !container.get(0)) return;

			// Calculate duration in frames
			const segmentDurationMs = (this._endTime - this._startTime) * 1000;
			const durationInFrames = Math.ceil((segmentDurationMs / 1000) * this._fps);

			// Create root only once
			if(!this._playerReactRoot){
				this._playerReactRoot = createRoot(container.get(0));
			}

			// Render or re-render with updated props
			// React will update existing component with new props (no remounting needed)

			// Create ref callback to capture player reference
			const self = this;
			const playerRef = function(player){
				self._playerRef = player;

				// After ref is set, seek to current playback cursor position
				if(player && self._currentPlaybackTime !== null){
					const frameNumber = self._calculateFrameFromTime(self._currentPlaybackTime);
					setTimeout(function(){
						player.seekTo(frameNumber);
						player.pause();
					}, 100);
				}
			};

			// Rollbar #1596/#1612: guard against non-number values that would pass the
			// truthy || check (e.g. objects {value: n}) and crash Remotion Player.
			const safeWidth = typeof this._videoWidth === 'number' && this._videoWidth > 0 ? this._videoWidth : 1920;
			const safeHeight = typeof this._videoHeight === 'number' && this._videoHeight > 0 ? this._videoHeight : 1080;
			this._playerReactRoot.render(
				React.createElement(Player, {
					ref: playerRef,
					component: VideoTrimPreview,
					compositionWidth: safeWidth,
					compositionHeight: safeHeight,
					fps: this._fps,
					durationInFrames: durationInFrames,
					inputProps: {
						videoUrl: this._videoUrl,
						startTimeMs: this._startTime * 1000,
						endTimeMs: this._endTime * 1000
					},
					style: { width: '100%', height: '100%' }
				})
			);
		},

		getStartTime: function(){
			return this._startTime * 1000; // Convert to milliseconds
		},

		getEndTime: function(){
			return this._endTime * 1000; // Convert to milliseconds
		},

		onRegionUpdated: function(){
			this._startTimeLabel$el.html(Utils.getInstance().formatPlaybackPositionToString(this._startTime, true) );
			this._endTimeLabel$el.html(Utils.getInstance().formatPlaybackPositionToString(this._endTime, true));

			this._timelineStartLabel$el.html(Utils.getInstance().formatPlaybackPositionToString(this._startTime));
			this._timelineEndLabel$el.html(Utils.getInstance().formatPlaybackPositionToString(this._endTime));
		},

		onVideoStartedPlaying : function(){
			this.$el.addClass("region_playing");
		},

		onVideoStoppedPlaying : function(){
			this.$el.removeClass("region_playing");
		},

		onSaveTrimButtonClicked : function(){
			this.trigger("saveTrim");
		},

	stopPlayback : function(){
	this._isPlaying = false;
		if(this._playerRef){
			this._playerRef.pause();
		}
		if(this._playbackMonitorInterval){
			clearInterval(this._playbackMonitorInterval);
			this._playbackMonitorInterval = null;
		}

	// Remove CSS class to toggle play/pause button visibility
	this.$el.removeClass("region_playing");
	},

		_onVideoMetadataLoaded: function(){
			if(this.videoPlayer$el && this.videoPlayer$el.get(0)){
				var video = this.videoPlayer$el.get(0);
				this._videoDuration = video.duration;
				this._videoUrl = _getBestVideoUrl(this.model);

		// Store actual video dimensions
		this._videoWidth = video.videoWidth || 1920;
		this._videoHeight = video.videoHeight || 1080;

		// Adjust player container width based on video aspect ratio
		this._adjustPlayerContainerSize();

				// Set initial trim region
				this._startTime = this.model.startTime ? this.model.startTime / 1000 : 0;
				this._endTime = this.model.endTime ? this.model.endTime / 1000 : this._videoDuration;

				// Initialize playback cursor at start position
				this._currentPlaybackTime = this._startTime;

				this._updateHandlePositions();
				this.onRegionUpdated();
				this._generateVideoThumbnails();

				// Render Remotion Player after video metadata is loaded
				this._renderRemotionPlayer();

				// Update cursor display after Remotion Player is ready
				setTimeout((function(){
					this._updatePlayCursorPosition(this._currentPlaybackTime);
				}).bind(this), 100);

				if(this._onVideoReadyPromiseResolve){
					this._onVideoReadyPromiseResolve();
				}

				setTimeout((function(){
					this.$el.removeClass("loading");
				}).bind(this), 0);
			}
		},

		_onVideoError: function(error){
			console.error("Video loading error:", error);
			if(this._onVideoReadyPromiseReject){
				this._onVideoReadyPromiseReject(new Error("Failed to load video"));
			}
		},

	_adjustPlayerContainerSize: function(){
		const playerContainer = this.$el.find(".video_buffer_player_container");
		if(!playerContainer || !this._videoWidth || !this._videoHeight) return;

		const videoAspectRatio = this._videoWidth / this._videoHeight;
		const containerHeight = playerContainer.height();
		const calculatedWidth = containerHeight * videoAspectRatio;

		// Set the container width to match video aspect ratio
		playerContainer.css("width", calculatedWidth + "px");
	},

		_generateVideoThumbnails: async function(){
			if(this._thumbnailsGenerated) return;

			const canvas = this.videoTimelineCanvas$el.get(0);
			if(!canvas) return;

			const video = this.videoPlayer$el.get(0);
			if(!video) return;

			const ctx = canvas.getContext('2d');
			const containerWidth = this.videoTimelineContainer$el.width();

			// Calculate canvas height based on video aspect ratio to prevent stretching
			const videoAspectRatio = video.videoWidth && video.videoHeight
				? video.videoWidth / video.videoHeight
				: 16 / 9; // Default to 16:9

			// Set desired thumbnail height for better visibility
			const desiredHeight = 70; // Increased for better visibility

			// Calculate thumbnail width based on desired height and aspect ratio
			const thumbnailWidth = desiredHeight * videoAspectRatio;
		const thumbnailCount = Math.floor(containerWidth / thumbnailWidth);

			const thumbnailHeight = desiredHeight;

			// Set canvas height to desired height
			const containerHeight = desiredHeight;

			canvas.width = containerWidth;
			canvas.height = containerHeight;
			this._timelineWidth = containerWidth;

			this._thumbnailsGenerated = true;

		// Store for dynamic thumbnail updates
		this._thumbnailCanvas = canvas;
		this._thumbnailCtx = ctx;
		this._thumbnailWidth = thumbnailWidth;
		this._thumbnailCount = thumbnailCount;
		this._thumbnailHeight = thumbnailHeight;
		this._containerHeight = containerHeight;

			// Try Mediabunny first, fall back to manual extraction if needed
			try {
			await this._generateFrameThumbnailsWithMediabunny(canvas, ctx, containerWidth, containerHeight, thumbnailCount, thumbnailWidth);
			} catch (error) {
				console.warn('Mediabunny thumbnail generation failed, using fallback:', error);
				await this._generateFrameThumbnailsFallback(video, canvas, ctx, containerWidth, containerHeight, thumbnailCount, thumbnailWidth);
			}
		},

	_generateFrameThumbnailsWithMediabunny: async function(canvas, ctx, containerWidth, containerHeight, thumbnailCount, thumbnailWidth){

			// Show loading indicator
			this.$el.addClass('generating-thumbnails');

			try {
			const totalStartTime = performance.now();

			// Extract thumbnails using VideoUtilsHelper (with Mediabunny acceleration)
			const thumbnailDataUrls = await VideoUtilsHelper.getInstance().extractThumbnails(
				_getBestVideoUrl(this.model),
				thumbnailCount,
				thumbnailWidth,
				containerHeight,
				(percent) => {
					// Progress callback - could update UI here
					console.log(`Thumbnail generation: ${Math.round(percent)}%`);
				}
			);

			// Load and draw thumbnail images onto canvas
			const imageLoadPromises = thumbnailDataUrls.map((dataUrl, index) => {
				return new RSVP.Promise((resolve) => {
					const img = new Image();
					img.onload = () => {
						// Calculate aspect ratio to cover thumbnail (like object-fit: cover)
						const scale = Math.max(
							thumbnailWidth / img.width,
							containerHeight / img.height
						);
						const scaledWidth = img.width * scale;
						const scaledHeight = img.height * scale;
						// Center the image within the thumbnail bounds
						const xOffset = (thumbnailWidth - scaledWidth) / 2;
						const yOffset = (containerHeight - scaledHeight) / 2;

						// Use clipping to create cover effect
						ctx.save();
						ctx.beginPath();
						ctx.rect(index * thumbnailWidth, 0, thumbnailWidth, containerHeight);
						ctx.clip();

						// Draw thumbnail (will be clipped to thumbnail bounds)
						ctx.drawImage(
							img,
							index * thumbnailWidth + xOffset,
							yOffset,
							scaledWidth,
							scaledHeight
						);

						ctx.restore();

						// Draw separator line
						if(index > 0){
							ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
							ctx.lineWidth = 1;
							ctx.beginPath();
							ctx.moveTo(index * thumbnailWidth, 0);
							ctx.lineTo(index * thumbnailWidth, containerHeight);
							ctx.stroke();
						}

						resolve();
					};
					img.src = dataUrl;
				});
			});

			// Wait for all thumbnails to be drawn (parallel image loading)
			const drawStartTime = performance.now();
			await RSVP.Promise.all(imageLoadPromises);
			const drawTime = performance.now() - drawStartTime;
			console.log(`Image load and draw time: ${drawTime.toFixed(0)}ms`);

			const totalTime = performance.now() - totalStartTime;
			console.log(`🎉 Total thumbnail generation completed in ${totalTime.toFixed(0)}ms (${(totalTime/1000).toFixed(2)}s)`);
			} catch (error) {
				console.error('Error generating thumbnails :', error);
				throw error;
			} finally {
				this.$el.removeClass('generating-thumbnails');
			}
		},

	_generateFrameThumbnailsFallback: async function(video, canvas, ctx, containerWidth, containerHeight, thumbnailCount, thumbnailWidth){
			let currentIndex = 0;

			return new Promise((resolve, reject) => {
				const generateNextThumbnail = () => {
					if(currentIndex >= thumbnailCount){
						// All thumbnails generated, restore video time
						video.currentTime = this._startTime;
						resolve();
						return;
					}

					const timePosition = (currentIndex / thumbnailCount) * this._videoDuration;
					video.currentTime = timePosition;

					const onSeeked = () => {
						video.removeEventListener('seeked', onSeeked);

						// Calculate aspect ratio to cover thumbnail (like object-fit: cover)
						const scale = Math.max(
							thumbnailWidth / video.videoWidth,
							containerHeight / video.videoHeight
						);
						const scaledWidth = video.videoWidth * scale;
						const scaledHeight = video.videoHeight * scale;

						// Center the image within the thumbnail bounds
						const xOffset = (thumbnailWidth - scaledWidth) / 2;
						const yOffset = (containerHeight - scaledHeight) / 2;

						// Draw video frame (with CORS error handling)
						try {
							// Use clipping to create cover effect
							ctx.save();
							ctx.beginPath();
							ctx.rect(currentIndex * thumbnailWidth, 0, thumbnailWidth, containerHeight);
							ctx.clip();

							ctx.drawImage(
								video,
								currentIndex * thumbnailWidth + xOffset,
								yOffset,
								scaledWidth,
								scaledHeight
							);

							ctx.restore();
						} catch (e) {
							// CORS error - canvas is tainted, draw solid color instead
							ctx.fillStyle = '#2a2a2a';
							ctx.fillRect(
								currentIndex * thumbnailWidth,
								0,
								thumbnailWidth,
								containerHeight
							);
						}

						// Draw separator line
						if(currentIndex > 0){
							ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
							ctx.lineWidth = 1;
							ctx.beginPath();
							ctx.moveTo(currentIndex * thumbnailWidth, 0);
							ctx.lineTo(currentIndex * thumbnailWidth, containerHeight);
							ctx.stroke();
						}

						currentIndex++;
						generateNextThumbnail();
					};

					video.addEventListener('seeked', onSeeked);
				};

				generateNextThumbnail();
			});
		},

	_updateThumbnailAtPosition: function(timePosition){
		// Only update if thumbnails have been generated and we have stored canvas context
		if(!this._thumbnailsGenerated || !this._thumbnailCanvas || !this._thumbnailCtx){
			return;
		}

		const video = this.videoPlayer$el && this.videoPlayer$el.get(0);
		if(!video) return;

		// Calculate which thumbnail index corresponds to this time position
		const percentPosition = timePosition / this._videoDuration;
		const thumbnailIndex = Math.floor(percentPosition * this._thumbnailCount);

		// Clamp to valid range
		const validIndex = Math.max(0, Math.min(thumbnailIndex, this._thumbnailCount - 1));

		// Set video to this time and wait for seek to complete
		video.currentTime = timePosition;

		const onSeeked = () => {
			video.removeEventListener('seeked', onSeeked);

			// Calculate aspect ratio to cover thumbnail (like object-fit: cover)
			const scale = Math.max(
				this._thumbnailWidth / video.videoWidth,
				this._containerHeight / video.videoHeight
			);
			const scaledWidth = video.videoWidth * scale;
			const scaledHeight = video.videoHeight * scale;

			// Center the image within the thumbnail bounds
			const xOffset = (this._thumbnailWidth - scaledWidth) / 2;
			const yOffset = (this._containerHeight - scaledHeight) / 2;

			// Draw video frame at the specific thumbnail position
			try {
				// Use clipping to create cover effect
				this._thumbnailCtx.save();
				this._thumbnailCtx.beginPath();
				this._thumbnailCtx.rect(
					validIndex * this._thumbnailWidth,
					0,
					this._thumbnailWidth,
					this._containerHeight
				);
				this._thumbnailCtx.clip();

				this._thumbnailCtx.drawImage(
					video,
					validIndex * this._thumbnailWidth + xOffset,
					yOffset,
					scaledWidth,
					scaledHeight
				);

				this._thumbnailCtx.restore();

				// Redraw separator line
				if(validIndex > 0){
					this._thumbnailCtx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
					this._thumbnailCtx.lineWidth = 1;
					this._thumbnailCtx.beginPath();
					this._thumbnailCtx.moveTo(validIndex * this._thumbnailWidth, 0);
					this._thumbnailCtx.lineTo(validIndex * this._thumbnailWidth, this._containerHeight);
					this._thumbnailCtx.stroke();
				}
			} catch (e) {
				console.warn('Error updating thumbnail:', e);
			}
		};

		video.addEventListener('seeked', onSeeked, { once: true });
	},

		renderVideo: function(){
			return new RSVP.Promise((function(resolve, reject){
				this._onVideoReadyPromiseResolve = resolve;
				this._onVideoReadyPromiseReject = reject;

				if(this.videoPlayer$el && this.videoPlayer$el.get(0)){
						// Note: Not setting crossorigin to avoid CORS issues
					this.videoPlayer$el.attr('src', _getBestVideoUrl(this.model));
					this.videoPlayer$el.get(0).load();
				}
			}).bind(this));
		},

		initVideoPlayer: function(){
			if(this.videoPlayer$el && this.videoPlayer$el.get(0)){
				var video = this.videoPlayer$el.get(0);

				$(video).on('loadedmetadata', this._onVideoMetadataLoaded.bind(this));
				$(video).on('play', this.onVideoStartedPlaying.bind(this));
				$(video).on('pause', this.onVideoStoppedPlaying.bind(this));
				$(video).on('ended', this.onVideoStoppedPlaying.bind(this));
				$(video).on('error', this._onVideoError.bind(this));
			}
		},

		render : function() {
			this.$el.html(this.compiledTemplate({"ti18n": ti18n}));
			this.setElement(this.$el.find("section").first());

			this.videoPlayer$el = this.$el.find(".video_player");
			this.remotionPlayerContainer$el = this.$el.find(".remotion_player_container");
			this.videoTimelineContainer$el = this.$el.find(".video_buffer_timeline_container");
			this.videoTimelineCanvas$el = this.$el.find(".video_timeline_canvas");

			this._startTimeLabel$el = this.$el.find(".trim_from_info");
			this._endTimeLabel$el = this.$el.find(".trim_to_info");

			this._timelineStartLabel$el = this.$el.find(".timeline_start_time");
			this._timelineEndLabel$el = this.$el.find(".timeline_end_time");

			this.saveTrimBtn$el = this.$el.find("#saveTrimBtn");
			this.$el.addClass("loading");

			this.initVideoPlayer();

			return this;
		},

		destroy: function(){
			this.stopPlayback();
			this._removeDragListeners();
			if(this._playbackMonitorInterval){
				clearInterval(this._playbackMonitorInterval);
			}
			// Unmount Remotion Player
			if(this._playerReactRoot){
				this._playerReactRoot.unmount();
				this._playerReactRoot = null;
			}
			if(this.videoPlayer$el && this.videoPlayer$el.get(0)){
				const video = this.videoPlayer$el.get(0);
				$(video).off();
				video.src = '';
				video.load();
			}
			this.remove();
		}

});

export default VideoBufferRegionSelectorView;
